import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import LazyImage from "@insite/mobius/LazyImage";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENProductDescription from "@tCentro/Components/CENProductDescription";
import { getDataLayerUtility } from "@tCentro/Custom/Utilities/getDataLayerUtility";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import ProductQuantityOrdered from "@tCentro/Overrides/Components/ProductQuantityOrdered";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useState, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    productSettings: getSettingsCollection(state).productSettings,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    extendedStyles?: ProductCarouselProductFullStyles;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductFullStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPriceStyles?: ProductPriceStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    productQuantityOrderedStylesWithError?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    addToListGridItem?: GridItemProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productInfoWrapper?: InjectableCss;
    qtyGridItem?: GridItemProps;
    addToCartContainer?: GridContainerProps;
    addToCartButtonGridItem?: GridItemProps;
    productPriceWrapper?: InjectableCss;
    productPrice?: InjectableCss;
    defaultProductImage?: InjectableCss;
    productSaveWrapper?: InjectableCss;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    subQTYBtn?: ButtonProps;
    addQTYBtn?: ButtonProps;
    tooltipContainerStyles?: InjectableCss;
    errorMsgStyle?: InjectableCss;
    errorIconStyles?: IconPresentationProps;
    errorMsgStyleTypo?: TypographyPresentationProps;
}

export const productCarouselProductFullStyles: ProductCarouselProductFullStyles = {
    errorMsgStyleTypo: {
        color: "danger",
        css: css`
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
        `,
    },
    errorIconStyles: {
        size: 20,
        css: css`
            span {
                width: 20px;
                height: 20px;
            }
        `,
    },
    errorMsgStyle: {
        css: css`
            display: flex;
            position: absolute;
            bottom: 8px;
            justify-content: center;
            gap: 4px;
            position: absolute;
            margin-top: 4px;
            top: 30px;
        `,
    },
    tooltipContainerStyles: {
        css: css`
            background-color: #262525;
            border-radius: 4px;
            width: max-content;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -34px;
            left: -10px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 8px 16px;
            color: #262525;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        top: -36px;
                        left: -12px;
                    `,
                    css`
                        top: -36px;
                        left: -12px;
                    `,
                    css``,
                    null,
                    null,
                ]);
            }}
            &:after {
                content: "";
                border-left: 0px solid ${getProp("theme.typography.p.color")};
                border-right: 4px solid ${getProp("theme.typography.p.color")};
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 42%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 16px solid ${getProp("theme.typography.p.color")};
            }
        `,
    },
    addQTYBtn: {
        css: css`
            height: 16px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 110px;
            margin-top: 2px;
            margin-bottom: 13px;
            z-index: 1;
            border: none;
            min-height: 16px !important;
            & svg {
                color: ${COLORS.black};
                height: 16px !important;
                width: 16px !important;
                margin-left: 5px;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: #e9e9e9;
            }
            &:active {
                background-color: transparent;
            }
            @media all and (max-width: 991px) and (min-width: 767px) {
                left: 98px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        left: 102px;
                    `,
                    css`
                        left: 102px;
                    `,
                    css``,
                    null,
                    null,
                ]);
            }}
        `,
    },
    subQTYBtn: {
        css: css`
            height: 16px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 48px;
            margin-top: 2px;
            margin-bottom: 12px;
            z-index: 1;
            border: none;
            min-height: 16px !important;
            & svg {
                color: ${COLORS.black};
                height: 16px !important;
                width: 16px !important;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled {
                background-color: transparent;
            }
            &:disabled:hover {
                background-color: transparent;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        left: 46px;
                    `,
                    css`
                        left: 46px;
                    `,
                    css``,
                    null,
                    null,
                ]);
            }}
            @media all and (max-width: 991px) and (min-width: 767px) {
                left: 48px;
            }
        `,
    },
    productPrice: {
        css: css`
            color: black;
            width: auto;
            font-weight: 600;
            font-size: 14px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    productSaveWrapper: {
        css: css`
            padding-bottom: 8px;
            div {
                justify-content: flex-start;
            }
        `,
    },
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            color: COLORS.black,
            typographyProps: {
                size: 12,
                css: css`
                    margin-bottom: 6px;
                    color: ${getProp("theme.typography.p.color")};
                    font-size: 18px !important;
                    line-height: 24px;
                    font-weight: 600;
                `,
            },
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 10px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
                label {
                    width: auto !important;
                }
            `,
        },
    },
    addToCartContainer: {
        gap: 12,
        css: css`
            position: relative;
            flex-grow: 0;
            margin-bottom: 6px;
            max-width: 100%;
            flex-basis: 100%;
            width: 100%;
            display: flex;
            align-items: flex-end;
            > div[class^="GridOffset"] {
                display: flex;
                align-items: center;
            }
        `,
    },
    qtyGridItem: {
        width: 3,
        css: css`
            max-width: 50%;
            flex-basis: 50%;
            align-items: baseline;
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                width: 60%;
                input {
                    text-align: center;
                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                    border: 2px solid ${COLORS.background.grayShade} !important;
                    margin-left: 2px;
                }
                @media all and (max-width: 1250px) and (min-width: 1160px) {
                    input {
                    }
                }
                @media all and (max-width: 991px) and (min-width: 767px) {
                    input {
                        width: 88px !important;
                    }
                }
                input:focus {
                    border-radius: 40px !important;
                }
            `,
        },
    },
    productQuantityOrderedStylesWithError: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                width: 60%;
                input {
                    text-align: center;
                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                    border: 2px solid ${COLORS.alertMsgRed} !important;
                    margin-left: 2px;
                }
                @media all and (max-width: 1250px) and (min-width: 1160px) {
                    input {
                    }
                }
                @media all and (max-width: 991px) and (min-width: 767px) {
                    input {
                        width: 88px !important;
                    }
                }
                input:focus {
                    border-radius: 40px !important;
                }
            `,
        },
    },
    addToCartButtonGridItem: {
        width: 7,
        css: css`
            max-width: 50%;
            flex-basis: 50%;
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
            border-radius: 16px;
            padding: 8px 16px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        span {
                            font-size: 10px;
                        }
                    `,
                    css`
                        span {
                            font-size: 10px;
                        }
                    `,
                    css`
                        padding: 0px;
                        margin-left: 28px;
                        span {
                            font-size: 10px;
                        }
                    `,
                    css`
                        span {
                            font-size: 10px;
                        }
                    `,
                    css``,
                ])}
        `,
        typographyProps: {
            size: 14,
        },
    },
    productAddToListLinkStyles: {
        link: {
            color: COLORS.background.tag,
            typographyProps: {
                size: 12,
                css: css`
                    margin-bottom: 8px;
                    color: ${COLORS.background.tag};
                    font-weight: 600;
                    display: flex;
                `,
            },
        },
    },
    productAvailabilityStyles: {
        messageText: {
            css: css`
                width: 100%;
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                color: ${getProp("theme.typography.p.color")};
                ${wrapInContainerStyles}
            `,
        },
        availabilityByWarehouseLink: {
            css: css`
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                line-height: 28px;
            `,
        },
    },
    defaultProductImage: {
        css: css`
            background: #fff;
            justify-content: center;
            img {
                width: 100%;
                max-height: 250px;
                object-fit: contain;
                height: auto;
                aspect-ratio: 1/1;
            }
        `,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProductFull = ({
    carouselId,
    product,
    extendedStyles,
    productInfo,
    productSettings,
    updateProductInfo,
    actionsTemplate,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    variantSelectionCompleted,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductFullStyles, extendedStyles));
    const [compareMinErr, setCompareMinErr] = useState(false);
    const [compareMultiErr, setCompareMultiErr] = useState(false);
    const productQtyOrderFinalStyles =
        compareMinErr || compareMultiErr
            ? { ...styles.productQuantityOrderedStylesWithError }
            : { ...styles.productQuantityOrderedStyles };

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo: productInfo ?? {} as ProductInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const [showTooltip, setShowTooltip] = useState(false);
    const [btnQty, setBtnQty] = useState(1);

   useEffect(() => {
        if (
            product?.detail?.multipleSaleQty &&
            product?.detail?.multipleSaleQty > 0 &&
            product?.detail?.roundingRule === "Multiple Only"
        ) {
            setBtnQty(product.detail.multipleSaleQty);
        } else {
            setBtnQty(productInfo?.qtyOrdered !== undefined ? productInfo.qtyOrdered : 1);
        }
    }, [product]);
   useEffect(() => {
        if (productContext.productInfo?.qtyOrdered !== undefined && productContext.productInfo?.qtyOrdered !== null) {
            setBtnQty(productContext.productInfo.qtyOrdered);
        }
    }, [productContext.productInfo?.qtyOrdered]);

    const addQtyFn = () => {
        setBtnQty(
            btnQty +
                (productContext.product?.detail?.multipleSaleQty &&
                productContext.product?.detail?.multipleSaleQty > 0 &&
                productContext.product?.detail?.roundingRule === "Multiple Only"
                    ? productContext.product?.detail?.multipleSaleQty
                    : 1),
        );
    };
    const subQtyFn = () => {
        const qtyValue =
            btnQty -
            (productContext.product?.detail?.multipleSaleQty &&
            productContext.product?.detail?.multipleSaleQty > 0 &&
            productContext.product?.detail?.roundingRule === "Multiple Only"
                ? productContext.product?.detail?.multipleSaleQty
                : 1);
        if (qtyValue === 0 || Math.sign(qtyValue) === -1) {
            setBtnQty(0);
            return false;
        }
        setBtnQty(qtyValue);
    };
    const handleMouseEnter = () => {
        if (
            productContext.product?.detail?.multipleSaleQty &&
            productContext.product?.detail?.multipleSaleQty > 0 &&
            productContext.product?.detail?.roundingRule === "Multiple Only" &&
            btnQty === productContext.product?.detail?.multipleSaleQty
        ) {
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

        const onClickProductHandler = () => {
            getDataLayerUtility({
                product: productContext.product,
                pricing: productContext.productInfo?.pricing,
                quantity: productContext.productInfo?.qtyOrdered || 1,
                itemObject: {
                    item_list_id: "compare_products_page",
                    item_list_name: "Compare Products: Products Page"
                },
            });
        }

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.productSaveWrapper}>
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.productInfoWrapper}>
                    {product.smallImagePath && product.mediumImagePath ? (
                        <ProductImage onClick={onClickProductHandler} product={productContext} extendedStyles={styles.productImageStyles} />
                    ) : (
                        <Clickable
                            href={productContext.productInfo.productDetailPath}
                            data-test-selector="productImage"
                            className="defaultProductImage"
                            {...styles.defaultProductImage}
                        >
                            <LazyImage
                                src="/userFiles/images/default-product-image.png"
                                altText="Default product image"
                            />
                        </Clickable>
                    )}
                    <StyledWrapper>
                        {product.brand && (
                            <StyledWrapper {...styles.productBrandWrapper} data-test-selector="productBrandWrapper">
                                <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                            </StyledWrapper>
                        )}

                        <ProductDescription onClick={onClickProductHandler} product={productContext} extendedStyles={styles.productDescriptionStyles} />

                        <ProductPartNumbers
                            data-test-selector="productMPNNumber"
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            showCustomerName={true}
                            showManufacturerItem={true}
                            showLabel={true}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                        <StyledWrapper>
                            <CENProductDescription product={productContext} />
                        </StyledWrapper>
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                        <StyledWrapper {...styles.productPriceWrapper}>
                            <ProductPrice
                                showLabel={false}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                product={productContext}
                                extendedStyles={styles.productPriceStyles}
                            />
                            {product.unitOfMeasures && (
                                <ProductUnitOfMeasureSelect
                                    labelPosition="left"
                                    extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                />
                            )}
                            <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <GridContainer {...styles.addToCartContainer}>
                    <GridItem {...styles.qtyGridItem}>
                        <Typography
                            as="label"
                            {...styles.productPrice}
                            htmlFor={`${carouselId}-qty`}
                            id={`${carouselId}-qty-label`}
                        >
                            {translate("QTY :")}
                        </Typography>
                        <Button
                            disabled={
                                !!(
                                    (!variantSelectionCompleted && productContext.product?.isVariantParent) ||
                                    (productContext.product?.detail?.multipleSaleQty &&
                                        productContext.product?.detail?.multipleSaleQty > 0 &&
                                        productContext.product?.detail?.roundingRule === "Multiple Only" &&
                                        btnQty === productContext.product?.detail?.multipleSaleQty)
                                )
                            }
                            {...styles.subQTYBtn}
                            data-test-selector="subQTYBtn"
                            onClick={subQtyFn}
                            onMouseOver={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <ButtonIcon src={Minus} />
                        </Button>
                        {showTooltip === true && (
                            <StyledWrapper data-test-selector="tooltip iam" {...styles.tooltipContainerStyles}>
                                <Typography
                                    as="span"
                                    css={css`
                                        color: ${getColor("info.contrast")} !important;
                                    `}
                                >
                                    {translate(`${productContext.product?.detail?.multipleSaleQty} unit minimum`)}
                                </Typography>
                            </StyledWrapper>
                        )}
                        <ProductQuantityOrdered
                            labelOverride=""
                            extendedStyles={productQtyOrderFinalStyles}
                            id={`${carouselId}-qty`}
                            onBtnclickQtyValue={btnQty}
                            setBtnQty={setBtnQty}
                            setCompareMinErr={setCompareMinErr}
                            setCompareMultiErr={setCompareMultiErr}
                        />
                        <Button
                            disabled={!variantSelectionCompleted && productContext.product?.isVariantParent}
                            {...styles.addQTYBtn}
                            data-test-selector="addQTYBtn"
                            onClick={addQtyFn}
                        >
                            <ButtonIcon src={Plus} />
                        </Button>
                        {compareMultiErr === true && (
                            <StyledWrapper {...styles.errorMsgStyle}>
                                <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                                <Typography {...styles.errorMsgStyleTypo}>
                                    {translate(
                                        `This product must be ordered in multiples of ${productContext.product?.detail?.multipleSaleQty}`,
                                    )}
                                </Typography>
                            </StyledWrapper>
                        )}
                        {compareMinErr === true && (
                            <StyledWrapper {...styles.errorMsgStyle}>
                                <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                                <Typography {...styles.errorMsgStyleTypo}>
                                    {translate(
                                        `This product has a minimum order of ${productContext.product?.detail?.multipleSaleQty} units`,
                                    )}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </GridItem>
                    <GridItem {...styles.addToCartButtonGridItem}>
                        <ProductAddToCartButton
                            data-test-selector="addToCartBtn"
                            extendedStyles={styles.productAddToCartButtonStyles}
                        />
                    </GridItem>
                </GridContainer>
                {actionsTemplate ? actionsTemplate(product) : []}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductFull);
